import {
  QueryClient,
  QueryFunctionContext,
  UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { GetServerSidePropsContext } from "next";

import httpClient from "helpers/hybridHttpClient";

import type {
  ExpandedStudyProgramResponse,
  StudyProgramCourseParams,
} from "types/courses/study-program";

import { STUDY_PROGRAM_QUERY_KEY_PREFIX } from "./constants";

interface StudyProgramQueryKeyFactoryOptions {
  params: StudyProgramCourseParams;
}

export const generateStudyProgramByCourseQueryKey = (
  options: StudyProgramQueryKeyFactoryOptions,
) => [STUDY_PROGRAM_QUERY_KEY_PREFIX, "course", options] as const;

type StudyProgramsByCourseQueryKey = ReturnType<
  typeof generateStudyProgramByCourseQueryKey
>;

const fetchStudyProgramsByCourse = async (
  params: StudyProgramCourseParams,
  ctx?: GetServerSidePropsContext,
) => {
  const { data } = await httpClient(ctx).get<ExpandedStudyProgramResponse[]>(
    "/api/courses-user-new/study-program/course/",
    {
      params,
    },
  );

  return data;
};

const studyProgramsByCourseQueryFnFactory =
  (ctx?: GetServerSidePropsContext) =>
  ({
    queryKey: [, , { params }],
  }: QueryFunctionContext<StudyProgramsByCourseQueryKey>) =>
    fetchStudyProgramsByCourse(params, ctx);

type useStudyProgramsByCourseQueryFetchOptions<TData> =
  StudyProgramQueryKeyFactoryOptions & {
    ctx?: GetServerSidePropsContext;
  } & Omit<
      UseQueryOptions<
        ExpandedStudyProgramResponse[],
        AxiosError,
        TData,
        StudyProgramsByCourseQueryKey
      >,
      "queryKey"
    > & {
      onSuccess?: (data: ExpandedStudyProgramResponse[]) => void;
    };

export const useStudyProgramsByCourse = <
  TData = ExpandedStudyProgramResponse[],
>({
  ctx,
  params,
  ...options
}: useStudyProgramsByCourseQueryFetchOptions<TData>) => {
  return useQuery({
    ...options,
    queryKey: generateStudyProgramByCourseQueryKey({
      params,
    }),
    queryFn: studyProgramsByCourseQueryFnFactory(ctx),
  });
};

useStudyProgramsByCourse.generateKey = generateStudyProgramByCourseQueryKey;

useStudyProgramsByCourse.fetchQuery = <TData = ExpandedStudyProgramResponse[]>(
  queryClient: QueryClient,
  { ctx, params, ...options }: useStudyProgramsByCourseQueryFetchOptions<TData>,
) =>
  queryClient.fetchQuery({
    ...options,
    queryKey: generateStudyProgramByCourseQueryKey({
      params,
    }),
    queryFn: studyProgramsByCourseQueryFnFactory(ctx),
  });

useStudyProgramsByCourse.prefetchQuery = <
  TData = ExpandedStudyProgramResponse[],
>(
  queryClient: QueryClient,
  { ctx, params, ...options }: useStudyProgramsByCourseQueryFetchOptions<TData>,
) =>
  queryClient.prefetchQuery({
    ...options,
    queryKey: generateStudyProgramByCourseQueryKey({
      params,
    }),
    queryFn: studyProgramsByCourseQueryFnFactory(ctx),
  });
